<script setup lang="ts">
import {
    watch, computed, onMounted, onBeforeUnmount, ref,
} from 'vue';
import {
    BChatMessage, BChatWelcome, BSkeletonLoader,
} from '@/design-system';
import type { ChatbotMessageType, SimulationProposalType } from '@/types/ChatbotMessageType';

const props = withDefaults(
    defineProps<{
        messages: ChatbotMessageType[];
        isTyping?: boolean;
        isLoading?: boolean;
        hideEmptyState?: boolean;
        isActive?: boolean;
    }>(),
    {
        isTyping: false,
        isLoading: false,
        hideEmptyState: false,
        isActive: false,
    },
);

const emit = defineEmits<{
    toggleReasoning: [messageId: string];
    selectProposal: [proposal: SimulationProposalType];
}>();

const NEAR_BOTTOM_THRESHOLD = 100;

const autoScrollEnabled = ref(true);
let prevScrollY = 0;

const distanceFromBottom = (): number => document.documentElement.scrollHeight - window.innerHeight - window.scrollY;

const isNearBottom = (): boolean => distanceFromBottom() <= NEAR_BOTTOM_THRESHOLD;

const handleScroll = (): void => {
    const currentScrollY = window.scrollY;

    // Manual scroll up disables auto-scroll; scrolling back to the bottom re-enables it.
    if (currentScrollY < prevScrollY) {
        autoScrollEnabled.value = false;
    }
    if (isNearBottom()) {
        autoScrollEnabled.value = true;
    }

    prevScrollY = currentScrollY;
};

const scrollToBottom = (behavior: ScrollBehavior = 'smooth'): void => {
    requestAnimationFrame(() => {
        if (allMessages.value.length === 0) {
            window.scrollTo({ top: 0 });

            return;
        }

        window.scrollTo({
            top: document.body.scrollHeight,
            behavior,
        });
    });
};

const typingMessage = computed(
    (): ChatbotMessageType => ({
        id: 'typing',
        text: '',
        sender: 'bot',
        timestamp: new Date().toISOString(),
        type: 'typing',
    }),
);

const allMessages = computed((): ChatbotMessageType[] => {
    const messages = [...props.messages];
    if (props.isTyping) {
        messages.push(typingMessage.value);
    }

    return messages;
});

onMounted(() => {
    prevScrollY = window.scrollY;
    window.addEventListener('scroll', handleScroll, { passive: true });
    scrollToBottom('instant');
});

onBeforeUnmount(() => {
    window.removeEventListener('scroll', handleScroll);
});

watch(
    () => props.isActive,
    newVal => {
        if (newVal) {
            autoScrollEnabled.value = true;
            scrollToBottom('instant');
        }
    },
);

// New message added → re-enable auto-scroll and jump to bottom.
watch(
    () => allMessages.value.length,
    () => {
        autoScrollEnabled.value = true;
        scrollToBottom();
    },
);

// Streaming chunk update → only follow if user hasn't scrolled up.
watch(
    () => {
        const msgs = allMessages.value;
        const streamingMsg = msgs.find(m => m.isStreaming);

        return streamingMsg?.streamingBuffer ?? null;
    },
    newVal => {
        if (newVal !== null && autoScrollEnabled.value) {
            scrollToBottom('instant');
        }
    },
);
</script>

<template>
    <div class="chat-interface d-flex flex-column">
        <div
            ref="messagesContainerRef"
            class="messages-container flex-grow-1 pa-4"
        >
            <slot name="prepend" />

            <div
                v-if="isLoading"
                class="loading-container"
            >
                <BSkeletonLoader
                    type="list-item-two-line"
                    class="mb-4"
                />
                <BSkeletonLoader
                    type="list-item-three-line"
                    class="mb-4"
                />
                <BSkeletonLoader type="list-item-two-line" />
            </div>

            <template v-else>
                <BChatMessage
                    v-for="message in allMessages"
                    :key="message.id"
                    :message="message"
                    @toggle-reasoning="(messageId: string) => emit('toggleReasoning', messageId)"
                />

                <BChatWelcome v-if="allMessages.length === 0 && !hideEmptyState" />
            </template>
        </div>

        <slot name="append" />
    </div>
</template>

<style scoped lang="scss">
.chat-interface {
    position: relative;
    min-height: calc(100vh - 96px);
}

.loading-container {
    max-width: 600px;
    margin: 0 auto;
    padding-top: 20px;
}
.chat-input-container {
    flex-shrink: 0;
}
</style>
